// LaTeX data from LaTeX Workshop extension
// These JSON files are loaded at build time and provide comprehensive command/environment data

import commandsData from '@assets/latex/commands.json'
import environmentsData from '@assets/latex/environments.json'
import unimathssymbolsData from '@assets/latex/unimathssymbols.json'

export interface LaTeXCommand {
  command: string
  snippet?: string
  detail?: string
}

export interface LaTeXEnvironment {
  text: string
}

export interface UnicodeMathSymbol {
  command: string
  detail?: string
  documentation?: string
}

// Type the imported JSON data
const commands = commandsData as Record<string, LaTeXCommand>
const environments = environmentsData as Record<string, LaTeXEnvironment>
const unimathssymbols = unimathssymbolsData as Record<string, UnicodeMathSymbol>

/**
 * Convert VS Code snippet syntax to plain text with cursor positions
 * ${1:label} -> placeholder with label
 * $1 -> placeholder without label
 * $0 -> final cursor position
 */
export function parseSnippet(snippet: string): { text: string; cursorPositions: number[]; finalCursorPos: number } {
  const cursorPositions: number[] = []
  let finalCursorPos = -1
  let text = ''
  let i = 0
  
  while (i < snippet.length) {
    if (snippet[i] === '$' && i + 1 < snippet.length) {
      if (snippet[i + 1] === '{') {
        // ${N:label} format
        const match = snippet.substring(i).match(/^\$\{(\d+)(?::([^}]+))?\}/)
        if (match) {
          const tabStop = parseInt(match[1])
          const label = match[2] || ''
          const placeholder = label || ''
          
          if (tabStop === 0) {
            finalCursorPos = text.length
          } else {
            cursorPositions[tabStop - 1] = text.length
            text += placeholder
          }
          
          i += match[0].length
          continue
        }
      } else if (/\d/.test(snippet[i + 1])) {
        // $N format
        const match = snippet.substring(i).match(/^\$(\d+)/)
        if (match) {
          const tabStop = parseInt(match[1])
          if (tabStop === 0) {
            finalCursorPos = text.length
          } else {
            cursorPositions[tabStop - 1] = text.length
          }
          i += match[0].length
          continue
        }
      }
    }
    
    // Handle escaped characters
    if (snippet[i] === '\\' && i + 1 < snippet.length) {
      // Check for escaped backslash or other escapes
      if (snippet[i + 1] === '\\') {
        text += '\\'
        i += 2
        continue
      }
    }
    
    text += snippet[i]
    i++
  }
  
  return { text, cursorPositions, finalCursorPos }
}

/**
 * Get all LaTeX commands
 */
export function getLaTeXCommands(): LaTeXCommand[] {
  return Object.values(commands)
}

/**
 * Get all LaTeX environments
 */
export function getLaTeXEnvironments(): string[] {
  return Object.values(environments).map(env => env.text)
}

/**
 * Get all Unicode math symbols
 */
export function getUnicodeMathSymbols(): UnicodeMathSymbol[] {
  return Object.values(unimathssymbols)
}

/**
 * Find commands matching a query
 */
export function findCommands(query: string): LaTeXCommand[] {
  const lowerQuery = query.toLowerCase()
  const matches: Array<{ command: LaTeXCommand; score: number }> = []
  
  for (const cmd of Object.values(commands)) {
    const cmdName = cmd.command.toLowerCase()
    let score = 0
    
    if (cmdName.startsWith(lowerQuery)) {
      score = 100 - cmdName.length // Prefer shorter exact matches
    } else if (cmdName.includes(lowerQuery)) {
      score = 50 - cmdName.length
    }
    
    if (score > 0) {
      matches.push({ command: cmd, score })
    }
  }
  
  return matches
    .sort((a, b) => b.score - a.score)
    .slice(0, 50)
    .map(m => m.command)
}

/**
 * Find environments matching a query
 */
export function findEnvironments(query: string): string[] {
  const lowerQuery = query.toLowerCase()
  const matches: Array<{ env: string; score: number }> = []
  
  // If query is empty, return all environments
  if (!lowerQuery) {
    return Object.values(environments)
      .map(env => env.text)
      .slice(0, 50)
  }
  
  for (const env of Object.values(environments)) {
    const envName = env.text.toLowerCase()
    let score = 0
    
    if (envName.startsWith(lowerQuery)) {
      score = 100 - envName.length
    } else if (envName.includes(lowerQuery)) {
      score = 50 - envName.length
    }
    
    if (score > 0) {
      matches.push({ env: env.text, score }) // Use original case
    }
  }
  
  return matches
    .sort((a, b) => b.score - a.score)
    .slice(0, 50)
    .map(m => m.env)
}

/**
 * Get command by name
 */
export function getCommand(commandName: string): LaTeXCommand | undefined {
  // Try exact match first
  for (const [key, cmd] of Object.entries(commands)) {
    if (cmd.command === commandName || key === commandName) {
      return cmd
    }
  }
  
  // Try case-insensitive match
  const lowerName = commandName.toLowerCase()
  for (const [key, cmd] of Object.entries(commands)) {
    if (cmd.command.toLowerCase() === lowerName || key.toLowerCase() === lowerName) {
      return cmd
    }
  }
  
  return undefined
}

export { commands, environments, unimathssymbols }

